import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/Dialog';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { ordersService } from '../services/orders';
import { toast } from 'sonner';

interface OrderInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderInfo: any;
  onSuccess: () => void;
}

const GATEWAY_OPTIONS = [
  { value: "Cartão de Crédito", name: "Cartão de Crédito" },
  { value: "Cartão de Crédito (Loja)", name: "Cartão de Crédito (Loja)" },
  { value: "Cartão de Crédito (PayPal Plus)", name: "Cartão de Crédito (PayPal Plus)" },
  { value: "Dinheiro", name: "Dinheiro" },
  { value: "Paypal (Link)", name: "Paypal (Link)" },
  { value: "Paypal (Loja)", name: "Paypal (Loja)" },
  { value: "Depósito", name: "Depósito" },
  { value: "Dinheiro + Depósito", name: "Dinheiro + Depósito" },
  { value: "Dinheiro + Cartão", name: "Dinheiro + Cartão" },
  { value: "Dinheiro + Cheque", name: "Dinheiro + Cheque" },
  { value: "Depósito + Cartão (Loja)", name: "Depósito + Cartão (Loja)" },
  { value: "Crédito Cliente (Loja)", name: "Crédito Cliente (Loja)" },
  { value: "Crédito Fornecedora (Loja)", name: "Crédito Fornecedora (Loja)" },
  { value: "Depósito + link PayPal", name: "Depósito + link PayPal" },
  { value: "Desconto / Fora do Closet", name: "Desconto / Fora do Closet" }
];

const PARCELED_OPTIONS = [
  { value: "vista", name: "À vista" },
  { value: "parceled", name: "Parcelado" },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: "", name: "-" },
  { value: "TED/PIX", name: "TED/PIX" },
  { value: "Espécie", name: "Espécie" },
  { value: "Rede", name: "Rede" },
  { value: "Paypal", name: "Paypal" },
  { value: "Crédito Fornecedora", name: "Crédito Fornecedora" },
  { value: "Crédito Cliente", name: "Crédito Cliente" },
  { value: "Depósito", name: "Depósito" },
  { value: "Desconto / Fora do Closet", name: "Desconto / Fora do Closet" }
];

export default function OrderInfoEditModal({
  isOpen,
  onClose,
  orderInfo,
  onSuccess
}: OrderInfoEditModalProps) {
  const [gateway, setGateway] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [payment1Val, setPayment1Val] = useState('');
  const [payment1Type, setPayment1Type] = useState('');
  const [payment2Val, setPayment2Val] = useState('');
  const [payment2Type, setPayment2Type] = useState('');
  const [payment3Val, setPayment3Val] = useState('');
  const [payment3Type, setPayment3Type] = useState('');
  const [payment4Val, setPayment4Val] = useState('');
  const [payment4Type, setPayment4Type] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (orderInfo) {
      setGateway(orderInfo.payment?.gateway || '');
      // Filtra produtos que não estão cancelados
      setProducts(
        (orderInfo.products || [])
          .filter((p: any) => !p.canceled_at)
          .map((p: any) => ({
            id: p.id,
            name: p.name,
            total: p.total,
            parceled: p.parceled || 'vista'
          }))
      );

      const pDetail = orderInfo.payment || {};
      setPayment1Val(pDetail.payment1?.value ? String(pDetail.payment1.value) : '');
      setPayment1Type(pDetail.payment1?.type || '');
      setPayment2Val(pDetail.payment2?.value ? String(pDetail.payment2.value) : '');
      setPayment2Type(pDetail.payment2?.type || '');
      setPayment3Val(pDetail.payment3?.value ? String(pDetail.payment3.value) : '');
      setPayment3Type(pDetail.payment3?.type || '');
      setPayment4Val(pDetail.payment4?.value ? String(pDetail.payment4.value) : '');
      setPayment4Type(pDetail.payment4?.type || '');
      setDiscount(orderInfo.discount ? String(orderInfo.discount) : '');
      setDiscountCode(orderInfo.discount_code || '');
    }
  }, [orderInfo, isOpen]);

  const handleProductParceledChange = (productId: number, val: string) => {
    setProducts(prev =>
      prev.map(p => (p.id === productId ? { ...p, parceled: val } : p))
    );
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        gateway,
        discount: discount ? parseFloat(discount) : 0,
        discount_code: discountCode || null,
        products: products.map(p => ({
          id: p.id,
          parceled: p.parceled,
          paymentType: p.parceled === 'parceled' ? 'parcelado' : 'avista'
        })),
        payment: {
          gateway,
          payment1: {
            value: payment1Val ? parseFloat(payment1Val) : null,
            type: payment1Type || null
          },
          payment2: {
            value: payment2Val ? parseFloat(payment2Val) : null,
            type: payment2Type || null
          },
          payment3: {
            value: payment3Val ? parseFloat(payment3Val) : null,
            type: payment3Type || null
          },
          payment4: {
            value: payment4Val ? parseFloat(payment4Val) : null,
            type: payment4Type || null
          }
        }
      };

      await ordersService.updateOrder(orderInfo.id, payload);
      toast.success('Pedido atualizado com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error(error);
      toast.error('Erro ao atualizar o pedido: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col p-6 bg-white rounded-lg">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-xl font-bold text-[#68686A]">Editar Pedido #{orderInfo?.id}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-[#68686A]">
          {/* Gateway/Forma de Pagamento */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Forma de Pagamento (Gateway)</label>
            <select
              value={gateway}
              onChange={e => setGateway(e.target.value)}
              className="w-full h-10 border border-gray-300 rounded px-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
            >
              {GATEWAY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          {/* Parcelamento de Produtos */}
          {products.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <label className="text-sm font-medium">Parcelamento de Produtos</label>
              <div className="space-y-2">
                {products.map(prod => (
                  <div key={prod.id} className="flex items-center justify-between gap-4 p-2 bg-gray-50 rounded">
                    <span className="text-xs font-medium truncate max-w-[200px]" title={prod.name}>
                      {prod.name}
                    </span>
                    <select
                      value={prod.parceled}
                      onChange={e => handleProductParceledChange(prod.id, e.target.value)}
                      className="h-8 border border-gray-300 rounded px-2 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
                    >
                      {PARCELED_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagamentos Parciais */}
          <div className="space-y-3 pt-2 border-t">
            <label className="text-sm font-medium">Detalhamento dos Pagamentos</label>
            
            <div className="grid grid-cols-2 gap-2">
              <span className="text-xs font-semibold">Valor (R$)</span>
              <span className="text-xs font-semibold">Tipo</span>
            </div>

            {/* Pagamento 1 */}
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="R$ 0,00"
                value={payment1Val}
                onChange={e => setPayment1Val(e.target.value)}
                className="h-9"
              />
              <select
                value={payment1Type}
                onChange={e => setPayment1Type(e.target.value)}
                className="h-9 border border-gray-300 rounded px-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
              >
                {PAYMENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Pagamento 2 */}
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="R$ 0,00"
                value={payment2Val}
                onChange={e => setPayment2Val(e.target.value)}
                className="h-9"
              />
              <select
                value={payment2Type}
                onChange={e => setPayment2Type(e.target.value)}
                className="h-9 border border-gray-300 rounded px-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
              >
                {PAYMENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Pagamento 3 */}
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="R$ 0,00"
                value={payment3Val}
                onChange={e => setPayment3Val(e.target.value)}
                className="h-9"
              />
              <select
                value={payment3Type}
                onChange={e => setPayment3Type(e.target.value)}
                className="h-9 border border-gray-300 rounded px-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
              >
                {PAYMENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Pagamento 4 */}
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                placeholder="R$ 0,00"
                value={payment4Val}
                onChange={e => setPayment4Val(e.target.value)}
                className="h-9"
              />
              <select
                value={payment4Type}
                onChange={e => setPayment4Type(e.target.value)}
                className="h-9 border border-gray-300 rounded px-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070]"
              >
                {PAYMENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Desconto */}
          <div className="space-y-3 pt-2 border-t">
            <label className="text-sm font-medium">Desconto</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Valor (R$)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={discount}
                  onChange={e => {
                    const val = e.target.value;
                    setDiscount(val);
                    if (parseFloat(val) > 0 && !discountCode) {
                      setDiscountCode('admin_free_shipping');
                    }
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Motivo do Desconto</label>
                <select
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value)}
                  disabled={!discount || parseFloat(discount) <= 0}
                  className="w-full h-9 border border-gray-300 rounded px-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4C070] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Selecione...</option>
                  <option value="admin_free_shipping">Frete Grátis</option>
                  <option value="admin_cash_payment">Desconto Pagamento Ato</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSubmitting} className="bg-[#D4C070] hover:bg-[#849D70] text-white">
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
